import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { buildSeo } from "@/lib/seo";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { STATES } from "@/data/states";
import { TAX_RATES } from "@/data/tax-rates";

export const Route = createFileRoute("/tools/payout-calculator")({
  head: () =>
    buildSeo({
      title: "Lottery Payout Calculator — Lump Sum vs Annuity by State",
      description:
        "Free lump-sum vs 30-year annuity payout calculator for Powerball, Mega Millions and any jackpot. Select your state and filing status to see net take-home after federal and state taxes.",
      path: "/tools/payout-calculator",
    }),
  component: PayoutCalculator,
});

type Filing = "single" | "mfj" | "hoh";

// 2025 federal brackets (approx, top-of-bracket → marginal rate)
const BRACKETS: Record<Filing, Array<[number, number]>> = {
  single: [
    [11_925, 0.10],
    [48_475, 0.12],
    [103_350, 0.22],
    [197_300, 0.24],
    [250_525, 0.32],
    [626_350, 0.35],
    [Infinity, 0.37],
  ],
  mfj: [
    [23_850, 0.10],
    [96_950, 0.12],
    [206_700, 0.22],
    [394_600, 0.24],
    [501_050, 0.32],
    [751_600, 0.35],
    [Infinity, 0.37],
  ],
  hoh: [
    [17_000, 0.10],
    [64_850, 0.12],
    [103_350, 0.22],
    [197_300, 0.24],
    [250_500, 0.32],
    [626_350, 0.35],
    [Infinity, 0.37],
  ],
};

function federalTax(income: number, filing: Filing): number {
  let tax = 0;
  let prev = 0;
  for (const [cap, rate] of BRACKETS[filing]) {
    if (income <= cap) {
      tax += (income - prev) * rate;
      return tax;
    }
    tax += (cap - prev) * rate;
    prev = cap;
  }
  return tax;
}

function fmtMoney(n: number) {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function PayoutCalculator() {
  const [jackpot, setJackpot] = useState<number>(100_000_000);
  const [cashRatio, setCashRatio] = useState<number>(0.49);
  const [stateSlug, setStateSlug] = useState<string>("california");
  const [filing, setFiling] = useState<Filing>("single");

  const stateOptions = useMemo(
    () => STATES.filter((s) => s.has_lottery).sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const stateTaxRate = useMemo(() => {
    const rec = TAX_RATES.find((t) => t.states?.slug === stateSlug);
    return (rec?.state_tax_rate ?? 0) / 100;
  }, [stateSlug]);

  const result = useMemo(() => {
    const cash = jackpot * cashRatio;
    const annualGross = jackpot / 30;

    // Lump sum
    const lumpFed = federalTax(cash, filing);
    const lumpState = cash * stateTaxRate;
    const lumpNet = cash - lumpFed - lumpState;

    // Annuity (tax each annual payment separately)
    const annFed = federalTax(annualGross, filing);
    const annState = annualGross * stateTaxRate;
    const annNet = annualGross - annFed - annState;

    return {
      cash,
      annualGross,
      lumpFed,
      lumpState,
      lumpNet,
      annFed,
      annState,
      annNet,
      annTotalNet: annNet * 30,
    };
  }, [jackpot, cashRatio, stateTaxRate, filing]);

  const stateName = stateOptions.find((s) => s.slug === stateSlug)?.name ?? "";

  return (
    <Layout>
      <article className="container max-w-5xl py-10 space-y-8">
        <header className="space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Lottery Payout Calculator: Lump Sum vs Annuity
          </h1>
          <p className="text-muted-foreground" data-speakable>
            Enter a jackpot, pick your state and filing status, and see exactly what you'd take
            home after federal and state taxes — for both the cash lump sum and the 30-year annuity.
          </p>
        </header>

        <Card>
          <CardContent className="pt-6 grid gap-4 md:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="jackpot">Advertised jackpot ($)</Label>
              <Input
                id="jackpot"
                type="number"
                min={1_000_000}
                step={1_000_000}
                value={jackpot}
                onChange={(e) => setJackpot(Number(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cash">Cash ratio</Label>
              <Select value={String(cashRatio)} onValueChange={(v) => setCashRatio(Number(v))}>
                <SelectTrigger id="cash"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.49">Powerball (≈49%)</SelectItem>
                  <SelectItem value="0.5">Mega Millions (≈50%)</SelectItem>
                  <SelectItem value="0.6">Other / custom (60%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="state">State of residence</Label>
              <Select value={stateSlug} onValueChange={setStateSlug}>
                <SelectTrigger id="state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stateOptions.map((s) => (
                    <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="filing">Filing status</Label>
              <Select value={filing} onValueChange={(v) => setFiling(v as Filing)}>
                <SelectTrigger id="filing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="mfj">Married filing jointly</SelectItem>
                  <SelectItem value="hoh">Head of household</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <section className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardContent className="pt-6 space-y-2">
              <h2 className="text-xl font-semibold">Lump Sum (Cash Option)</h2>
              <p className="text-sm text-muted-foreground">
                Take {Math.round(cashRatio * 100)}% of the advertised jackpot today, taxed all at once.
              </p>
              <Table>
                <TableBody>
                  <Row label="Cash value" value={fmtMoney(result.cash)} />
                  <Row label="Federal tax" value={`− ${fmtMoney(result.lumpFed)}`} />
                  <Row label={`State tax (${(stateTaxRate * 100).toFixed(2)}%)`} value={`− ${fmtMoney(result.lumpState)}`} />
                  <Row label="Net take-home" value={fmtMoney(result.lumpNet)} bold />
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 space-y-2">
              <h2 className="text-xl font-semibold">30-Year Annuity</h2>
              <p className="text-sm text-muted-foreground">
                One payment per year for 30 years (simplified — actual payments grow 5% annually).
              </p>
              <Table>
                <TableBody>
                  <Row label="Annual gross" value={fmtMoney(result.annualGross)} />
                  <Row label="Federal tax / yr" value={`− ${fmtMoney(result.annFed)}`} />
                  <Row label={`State tax / yr (${(stateTaxRate * 100).toFixed(2)}%)`} value={`− ${fmtMoney(result.annState)}`} />
                  <Row label="Annual net" value={fmtMoney(result.annNet)} bold />
                  <Row label="30-year total net" value={fmtMoney(result.annTotalNet)} bold />
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-2 rounded-md border p-4 bg-secondary/30">
          <h2 className="text-lg font-semibold">Which is better for {stateName}?</h2>
          <p className="text-sm text-muted-foreground">
            On these inputs, the annuity nets {fmtMoney(result.annTotalNet - result.lumpNet)}{" "}
            {result.annTotalNet > result.lumpNet ? "more" : "less"} than the lump sum over 30 years
            — before accounting for investment returns on the cash option, inflation, or your
            personal tax situation in future years.
          </p>
        </section>

        <section className="space-y-2 text-sm">
          <h2 className="text-lg font-semibold">Related tools & guides</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><Link to="/powerball/jackpot-analysis" className="text-primary hover:underline">Powerball jackpot analysis — every state</Link></li>
            <li><Link to="/mega-millions/jackpot-analysis" className="text-primary hover:underline">Mega Millions jackpot analysis — every state</Link></li>
            <li><Link to="/tools/tax-calculator" className="text-primary hover:underline">State-by-state tax calculator</Link></li>
            <li><Link to="/powerball/how-to-play" className="text-primary hover:underline">How to play Powerball</Link></li>
            <li><Link to="/mega-millions/how-to-play" className="text-primary hover:underline">How to play Mega Millions</Link></li>
          </ul>
          <p className="text-xs text-muted-foreground pt-2">
            Estimates only. Federal brackets reflect 2025 IRS rates; state tax uses each state's top
            marginal rate. Local/municipal taxes (e.g. NYC) are not included. Consult a tax
            professional before claiming a prize.
          </p>
        </section>
      </article>
    </Layout>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <TableRow>
      <TableCell className={bold ? "font-semibold" : ""}>{label}</TableCell>
      <TableCell className={`text-right ${bold ? "font-semibold" : ""}`}>{value}</TableCell>
    </TableRow>
  );
}